import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API = process.env.REACT_APP_API || "/api";

/* ------------------------------------------------------------------ utils */

const nf = (d) =>
  new Intl.NumberFormat("fr-CH", { minimumFractionDigits: d, maximumFractionDigits: d });

const chf = (v) => (v == null ? "—" : nf(2).format(v));
const chf4 = (v) => (v == null ? "—" : nf(4).format(v));

const weight = (g) => {
  if (g == null) return "—";
  if (g >= 1000) return `${nf(g % 1000 === 0 ? 0 : 3).format(g / 1000)} kg`;
  return `${nf(g % 1 === 0 ? 0 : 1).format(g)} g`;
};

const perKg = (pricePerGram) =>
  pricePerGram ? `${nf(2).format(pricePerGram * 1000)} / kg` : null;

async function call(path, { token, method = "GET", body } = {}) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = null;
  }
  if (!res.ok) throw new Error((data && data.detail) || `Erreur ${res.status}`);
  return data;
}

/* Palette des strates : teal pour les pâtes, ambre pour les appareils sucrés,
   la position dans la liste décide, comme les couches d'un feuilletage. */
const STRATA = [
  "#0D6E63", "#15837A", "#2F9186", "#4C9E8E",
  "#C2621F", "#D57728", "#E8541F", "#B34A1B",
  "#5A6B6E", "#77888B",
];

/* ------------------------------------------------------------------ login */

function Login({ onIn }) {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onIn(await call("/login", { method: "POST", body: { user, password } }));
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <div className="gate-card">
        <h1 className="gate-title">Recettes et coût des cours</h1>
        <p className="gate-sub">
          Les coûts viennent des prix réellement payés aux fournisseurs, remis à
          jour chaque soir.
        </p>
        <label className="field">
          <span>Identifiant</span>
          <input
            value={user}
            autoFocus
            onChange={(e) => setUser(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </label>
        <label className="field">
          <span>Mot de passe</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </label>
        {error && <p className="gate-error">{error}</p>}
        <button className="btn-main" onClick={submit} disabled={busy || !user}>
          {busy ? "Connexion…" : "Se connecter"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- stratigraphie */

function Stratum({ node, color, share, open, onToggle, showCosts }) {
  const leaves = node.children || [];
  return (
    <div className={`stratum ${open ? "is-open" : ""}`}>
      <button
        className="stratum-band"
        style={{ "--band": color, "--share": `${share}%` }}
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="band-fill" />
        <span className="band-body">
          <span className="band-name">
            {node.name}
            {leaves.length > 0 && (
              <span className="band-count">{leaves.length} ingrédients</span>
            )}
          </span>
          <span className="band-figures">
            <span className="band-qty">{weight(node.qty)}</span>
            {showCosts && <span className="band-cost">{chf(node.cost)}</span>}
          </span>
        </span>
      </button>

      {open && leaves.length > 0 && (
        <table className="leaves">
          <tbody>
            {leaves.map((leaf, i) => (
              <tr key={`${leaf.code}-${i}`} className={leaf.priced === false ? "no-price" : ""}>
                <td className="l-name">
                  {leaf.name}
                  <em>{leaf.code}</em>
                </td>
                <td className="l-qty">{weight(leaf.qty)}</td>
                {showCosts && (
                  <td className="l-unit">
                    {leaf.priced === false ? "prix manquant" : perKg(leaf.unit_cost)}
                  </td>
                )}
                <td className="l-sup">{leaf.supplier || ""}</td>
                {showCosts && <td className="l-cost">{chf(leaf.cost)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- course panel */

function Stepper({ label, value, onChange, min = 1, max = 200, step = 1, hint }) {
  return (
    <div className="stepper">
      <span className="stepper-label">
        {label}
        {hint && <em>{hint}</em>}
      </span>
      <div className="stepper-ctl">
        <button onClick={() => onChange(Math.max(min, +(value - step).toFixed(2)))}>–</button>
        <input
          value={value}
          inputMode="decimal"
          onChange={(e) => {
            const v = parseFloat(e.target.value.replace(",", "."));
            if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
            else if (e.target.value === "") onChange(min);
          }}
        />
        <button onClick={() => onChange(Math.min(max, +(value + step).toFixed(2)))}>+</button>
      </div>
    </div>
  );
}

function CoursePanel({ course, params, setParams, showCosts, loading }) {
  if (!showCosts) {
    return (
      <aside className="panel">
        <h2 className="panel-title">Quantités du cours</h2>
        <p className="panel-note">
          Demandez un accès enseignant pour voir les coûts matière.
        </p>
      </aside>
    );
  }

  return (
    <aside className="panel">
      <h2 className="panel-title">Calcul du cours</h2>

      <Stepper
        label="Participants"
        value={params.participants}
        onChange={(v) => setParams({ ...params, participants: Math.round(v) })}
      />
      <Stepper
        label="Par équipe"
        value={params.team_size}
        max={12}
        onChange={(v) => setParams({ ...params, team_size: Math.round(v) })}
      />
      <Stepper
        label="Batches par équipe"
        value={params.batches_per_team}
        min={1}
        max={20}
        hint="une équipe pétrit une pâte entière"
        onChange={(v) => setParams({ ...params, batches_per_team: Math.round(v) })}
      />

      {course && (
        <>
          <dl className="readout">
            <div>
              <dt>Équipes</dt>
              <dd>{course.teams}</dd>
            </div>
            <div>
              <dt>Batches à produire</dt>
              <dd>{course.batches}</dd>
            </div>
            <div>
              <dt>Pièces sorties</dt>
              <dd>{nf(0).format(course.pieces)}</dd>
            </div>
            <div>
              <dt>Par participant</dt>
              <dd>{nf(1).format(course.pieces_per_participant)}</dd>
            </div>
          </dl>

          <div className="headline">
            <span className="headline-label">Coût matière du cours</span>
            <span className="headline-value">
              {chf(course.total_cost)}
              <em>{course.currency}</em>
            </span>
            <span className="headline-foot">
              {chf(course.cost_per_participant)} par participant ·{" "}
              {chf(course.cost_per_piece)} la pièce
            </span>
          </div>

          {course.by_supplier && course.by_supplier.length > 0 && (
            <ul className="suppliers">
              {course.by_supplier.map((s) => (
                <li key={s.supplier}>
                  <span>{s.supplier}</span>
                  <span>{chf(s.cost)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {loading && <p className="panel-note">Calcul…</p>}
    </aside>
  );
}

/* ------------------------------------------------------------ shopping list */

function ShoppingList({ items, showCosts, batches }) {
  if (!items || items.length === 0) return null;
  const total = items.reduce((s, i) => s + (i.cost || 0), 0);
  return (
    <section className="list">
      <h2 className="list-title">
        Matière à sortir
        <span>{batches > 1 ? `${batches} batches` : "un batch"}</span>
      </h2>
      <table className="table">
        <thead>
          <tr>
            <th>Ingrédient</th>
            <th>Fournisseur</th>
            <th className="num">Quantité</th>
            {showCosts && <th className="num">Prix</th>}
            {showCosts && <th className="num">Coût</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((i, k) => (
            <tr key={`${i.code}-${k}`} className={i.priced === false ? "no-price" : ""}>
              <td>
                {i.name}
                <em>{i.code}</em>
              </td>
              <td className="sup">{i.supplier || "—"}</td>
              <td className="num">{weight(i.qty)}</td>
              {showCosts && (
                <td className="num soft">
                  {i.priced === false ? "prix manquant" : perKg(i.unit_cost)}
                </td>
              )}
              {showCosts && <td className="num">{chf(i.cost)}</td>}
            </tr>
          ))}
        </tbody>
        {showCosts && (
          <tfoot>
            <tr>
              <td colSpan={4}>Total</td>
              <td className="num">{chf(total)}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </section>
  );
}

/* ----------------------------------------------------------------- the page */

export default function App() {
  const [session, setSession] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [code, setCode] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [course, setCourse] = useState(null);
  const [open, setOpen] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useState({
    participants: 10,
    team_size: 2,
    batches_per_team: 1,
  });

  const showCosts = session && session.role !== "student";
  const timer = useRef(null);

  useEffect(() => {
    if (!session) return;
    call("/recipes", { token: session.token })
      .then((d) => {
        setRecipes(d.recipes || []);
        if (d.recipes && d.recipes.length && !code) setCode(d.recipes[0].code);
      })
      .catch((e) => setError(e.message));
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!session || !code) return;
    setBusy(true);
    setOpen({});
    call(`/recipe/${code}`, { token: session.token })
      .then(setRecipe)
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [session, code]);

  const loadCourse = useCallback(() => {
    if (!session || !code || !showCosts) return;
    const q = new URLSearchParams({
      code,
      participants: params.participants,
      team_size: params.team_size,
      batches_per_team: params.batches_per_team,
    });
    call(`/course?${q.toString()}`, { token: session.token })
      .then(setCourse)
      .catch((e) => setError(e.message));
  }, [session, code, params, showCosts]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(loadCourse, 250);
    return () => clearTimeout(timer.current);
  }, [loadCourse]);

  const strata = useMemo(() => {
    if (!recipe) return [];
    const items = recipe.components || [];
    const max = Math.max(...items.map((c) => c.qty || 0), 1);
    return items.map((c, i) => ({
      node: c,
      color: STRATA[i % STRATA.length],
      share: Math.max(6, ((c.qty || 0) / max) * 100),
    }));
  }, [recipe]);

  if (!session) return <><Style /><Login onIn={setSession} /></>;

  const listItems = course ? course.shopping_list : recipe && recipe.shopping_list;
  const batches = course ? course.batches : 1;

  return (
    <>
      <Style />
      <header className="top">
        <div className="top-left">
          <select value={code || ""} onChange={(e) => setCode(e.target.value)}>
            {recipes.map((r) => (
              <option key={r.code} value={r.code}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div className="top-right">
          <span>{session.name}</span>
          <button className="btn-plain" onClick={() => setSession(null)}>
            Quitter
          </button>
        </div>
      </header>

      {error && (
        <div className="alert">
          {error}
          <button onClick={() => setError(null)}>Fermer</button>
        </div>
      )}

      {recipe && (
        <main className="page">
          <div className="main-col">
            <div className="recipe-head">
              <h1>{recipe.name}</h1>
              <p className="recipe-meta">
                {recipe.code} · un batch donne {nf(0).format(recipe.batch_qty)}{" "}
                {recipe.uom === "Unité(s)" ? "pièces" : recipe.uom}
                {showCosts && (
                  <>
                    {" · "}
                    <strong>{chf(recipe.total_cost)}</strong> le batch,{" "}
                    <strong>{chf(recipe.cost_per_piece)}</strong> la pièce
                  </>
                )}
              </p>
              {recipe.unpriced && recipe.unpriced.length > 0 && showCosts && (
                <p className="warn">
                  Sans prix : {recipe.unpriced.join(", ")}. Le coût affiché est donc
                  un minimum.
                </p>
              )}
            </div>

            <div className="strata">
              {strata.map(({ node, color, share }, i) => (
                <Stratum
                  key={`${node.code}-${i}`}
                  node={node}
                  color={color}
                  share={share}
                  showCosts={showCosts}
                  open={!!open[i]}
                  onToggle={() => setOpen({ ...open, [i]: !open[i] })}
                />
              ))}
            </div>

            {recipe.nutrition && recipe.nutrition.allergens && (
              <p className="allergens">Allergènes : {recipe.nutrition.allergens}</p>
            )}

            <ShoppingList items={listItems} showCosts={showCosts} batches={batches} />
          </div>

          <CoursePanel
            course={course}
            params={params}
            setParams={setParams}
            showCosts={showCosts}
            loading={busy}
          />
        </main>
      )}

      {!recipe && !error && <p className="page-note">Chargement de la recette…</p>}
    </>
  );
}

/* --------------------------------------------------------------------- CSS */

function Style() {
  return (
    <style>{`
:root{
  --ink:#101418; --paper:#ffffff; --wash:#F1F2F0; --rule:#E0E3E0;
  --muted:#6B7076; --teal:#0D6E63; --orange:#E8541F;
  --font:"Helvetica Neue",Helvetica,Arial,sans-serif;
}
*{box-sizing:border-box}
body{margin:0;background:var(--wash);color:var(--ink);font-family:var(--font);
  font-size:15px;line-height:1.45;-webkit-font-smoothing:antialiased;
  font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1}
button,input,select{font:inherit;color:inherit}
button{cursor:pointer}
:focus-visible{outline:2px solid var(--orange);outline-offset:2px}

/* gate */
.gate{min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px}
.gate-card{background:var(--paper);width:100%;max-width:380px;padding:32px;
  border:1px solid var(--rule)}
.gate-title{margin:0 0 6px;font-size:24px;line-height:1.15;letter-spacing:-.01em}
.gate-sub{margin:0 0 24px;color:var(--muted);font-size:14px}
.field{display:block;margin-bottom:14px}
.field span{display:block;font-size:13px;color:var(--muted);margin-bottom:4px}
.field input{width:100%;padding:9px 10px;border:1px solid var(--rule);background:var(--paper)}
.gate-error{color:var(--orange);font-size:14px;margin:0 0 12px}
.btn-main{width:100%;padding:11px;background:var(--ink);color:#fff;border:0}
.btn-main:disabled{opacity:.4}

/* top bar */
.top{display:flex;justify-content:space-between;align-items:center;gap:16px;
  padding:12px 24px;background:var(--paper);border-bottom:1px solid var(--rule);
  position:sticky;top:0;z-index:20}
.top select{border:0;background:transparent;font-size:16px;font-weight:600;max-width:60vw}
.top-right{display:flex;align-items:center;gap:14px;color:var(--muted);font-size:14px}
.btn-plain{background:none;border:0;color:var(--muted);text-decoration:underline}

.alert{margin:16px 24px;padding:10px 14px;background:#fff;border-left:3px solid var(--orange);
  display:flex;justify-content:space-between;gap:16px;font-size:14px}
.alert button{background:none;border:0;color:var(--muted)}
.page-note{padding:32px 24px;color:var(--muted)}

/* layout */
.page{display:grid;grid-template-columns:minmax(0,1fr) 320px;gap:28px;
  padding:28px 24px 64px;max-width:1280px;margin:0 auto;align-items:start}
.main-col{min-width:0}

.recipe-head h1{margin:0 0 4px;font-size:34px;line-height:1.08;letter-spacing:-.02em;
  max-width:20ch}
.recipe-meta{margin:0;color:var(--muted);font-size:14px}
.recipe-meta strong{color:var(--ink);font-weight:600}
.warn{margin:8px 0 0;font-size:13px;color:var(--orange)}

/* strates */
.strata{margin-top:24px;background:var(--paper);border:1px solid var(--rule)}
.stratum + .stratum{border-top:1px solid var(--rule)}
.stratum-band{display:block;width:100%;text-align:left;background:none;border:0;
  padding:0;position:relative}
.band-fill{position:absolute;left:0;top:0;bottom:0;width:var(--share);
  background:var(--band);opacity:.13}
.stratum.is-open .band-fill{opacity:.22}
.stratum-band::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;
  background:var(--band)}
.band-body{position:relative;display:flex;justify-content:space-between;gap:16px;
  align-items:baseline;padding:14px 16px 14px 19px}
.band-name{font-weight:600}
.band-count{display:block;font-weight:400;font-size:12px;color:var(--muted);margin-top:1px}
.band-figures{display:flex;gap:18px;align-items:baseline;white-space:nowrap}
.band-qty{color:var(--teal);font-weight:600}
.band-cost{min-width:64px;text-align:right;font-weight:600}

.leaves{width:100%;border-collapse:collapse;background:#FAFBFA}
.leaves td{padding:7px 16px;border-top:1px solid var(--rule);font-size:14px;vertical-align:top}
.leaves .l-name em{display:block;font-style:normal;font-size:11px;color:var(--muted)}
.leaves .l-qty{color:var(--teal);white-space:nowrap;width:90px}
.leaves .l-unit{color:var(--muted);white-space:nowrap;width:120px}
.leaves .l-sup{color:var(--muted)}
.leaves .l-cost{text-align:right;white-space:nowrap;width:80px}
.no-price{background:#FFF6F2}

.allergens{margin:18px 0 0;font-size:14px;color:var(--muted)}

/* panneau cours */
.panel{position:sticky;top:76px;background:var(--paper);border:1px solid var(--rule);
  padding:20px}
.panel-title{margin:0 0 16px;font-size:17px}
.panel-note{color:var(--muted);font-size:14px;margin:8px 0 0}

.stepper{display:flex;justify-content:space-between;align-items:center;gap:12px;
  padding:8px 0;border-bottom:1px solid var(--rule)}
.stepper-label{font-size:14px}
.stepper-label em{display:block;font-style:normal;font-size:12px;color:var(--muted);
  max-width:18ch}
.stepper-ctl{display:flex;align-items:center;border:1px solid var(--rule)}
.stepper-ctl button{width:30px;height:32px;background:none;border:0;color:var(--muted);
  font-size:17px;line-height:1}
.stepper-ctl input{width:46px;height:32px;border:0;border-left:1px solid var(--rule);
  border-right:1px solid var(--rule);text-align:center;background:none}

.readout{margin:18px 0 0;display:grid;grid-template-columns:1fr 1fr;gap:12px 8px}
.readout dt{font-size:12px;color:var(--muted)}
.readout dd{margin:0;font-size:20px;font-weight:600}

.headline{margin-top:20px;padding-top:16px;border-top:2px solid var(--ink)}
.headline-label{display:block;font-size:13px;color:var(--muted)}
.headline-value{display:block;font-size:40px;line-height:1.05;letter-spacing:-.025em;
  font-weight:600;margin-top:2px}
.headline-value em{font-style:normal;font-size:15px;font-weight:400;color:var(--muted);
  margin-left:7px;letter-spacing:0}
.headline-foot{display:block;margin-top:6px;font-size:13px;color:var(--muted)}

.suppliers{list-style:none;margin:18px 0 0;padding:0;font-size:13px}
.suppliers li{display:flex;justify-content:space-between;gap:12px;padding:5px 0;
  border-top:1px solid var(--rule);color:var(--muted)}
.suppliers li span:last-child{color:var(--ink)}

/* liste de matière */
.list{margin-top:36px}
.list-title{font-size:17px;margin:0 0 12px;display:flex;justify-content:space-between;
  align-items:baseline;gap:16px}
.list-title span{font-size:13px;color:var(--muted);font-weight:400}
.table{width:100%;border-collapse:collapse;background:var(--paper);
  border:1px solid var(--rule)}
.table th{text-align:left;font-size:12px;color:var(--muted);font-weight:400;
  padding:9px 14px;border-bottom:1px solid var(--rule)}
.table td{padding:9px 14px;border-top:1px solid var(--rule);font-size:14px}
.table tbody tr:first-child td{border-top:0}
.table td em{display:block;font-style:normal;font-size:11px;color:var(--muted)}
.table .num{text-align:right;white-space:nowrap}
.table .soft{color:var(--muted)}
.table .sup{color:var(--muted)}
.table tfoot td{border-top:2px solid var(--ink);font-weight:600}

@media (max-width:900px){
  .page{grid-template-columns:1fr;padding:20px 14px 48px}
  .panel{position:static;order:-1}
  .recipe-head h1{font-size:26px}
  .leaves .l-sup{display:none}
  .table .sup{display:none}
}
@media (prefers-reduced-motion:reduce){*{transition:none!important;animation:none!important}}
    `}</style>
  );
}
